import { auth } from './firebase';
import { 
  sendPasswordResetEmail, 
  updatePassword, 
  EmailAuthProvider, 
  reauthenticateWithCredential 
} from 'firebase/auth';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from './firebase';

/**
 * Send password reset email to user
 * @param {string} email - User's email address
 * @returns {Promise<Object>} - Success/error result
 */
export const sendPasswordReset = async (email) => {
  try {
    await sendPasswordResetEmail(auth, email);
    return { success: true, message: 'Password reset email sent successfully!' };
  } catch (error) {
    console.error('Error sending password reset email:', error);
    return { 
      success: false, 
      message: error.code === 'auth/user-not-found' 
        ? 'No account found with this email address.' 
        : 'Failed to send password reset email. Please try again.' 
    };
  }
};

/**
 * Admin reset user password (generates temporary password)
 * @param {string} userId - User's UID
 * @param {string} userEmail - User's email address
 * @returns {Promise<Object>} - Success/error result with temporary password
 */
export const adminResetPassword = async (userId, userEmail) => {
  try {
    // Generate a secure temporary password
    const tempPassword = generateSecurePassword();
    
    // Update user's password in Firebase Auth
    // Note: This requires admin SDK in production, but for now we'll use email reset
    await sendPasswordResetEmail(auth, userEmail);
    
    // Store temporary password in Firestore for admin reference
    await updateDoc(doc(db, 'users', userId), {
      tempPassword: tempPassword,
      tempPasswordCreated: new Date(),
      passwordResetByAdmin: true
    });
    
    return { 
      success: true, 
      message: 'Password reset email sent to user. Temporary password generated for admin reference.',
      tempPassword: tempPassword
    };
  } catch (error) {
    console.error('Error in admin password reset:', error);
    return { 
      success: false, 
      message: 'Failed to reset password. Please try again.' 
    };
  }
};

/**
 * User changes their own password (requires current password)
 * @param {string} currentPassword - User's current password
 * @param {string} newPassword - User's new password
 * @returns {Promise<Object>} - Success/error result
 */
export const changeUserPassword = async (currentPassword, newPassword) => {
  try {
    const user = auth.currentUser;
    if (!user || !user.email) {
      return { success: false, message: 'No authenticated user found.' };
    }

    // Re-authenticate user with current password
    const credential = EmailAuthProvider.credential(user.email, currentPassword);
    await reauthenticateWithCredential(user, credential);
    
    // Update password
    await updatePassword(user, newPassword);
    
    // Clear any temporary password flags
    await updateDoc(doc(db, 'users', user.uid), {
      tempPassword: null,
      tempPasswordCreated: null,
      passwordResetByAdmin: false
    });
    
    return { success: true, message: 'Password updated successfully!' };
  } catch (error) {
    console.error('Error changing password:', error);
    let message = 'Failed to update password.';
    
    if (error.code === 'auth/wrong-password') {
      message = 'Current password is incorrect.';
    } else if (error.code === 'auth/weak-password') {
      message = 'New password is too weak. Please choose a stronger password.';
    } else if (error.code === 'auth/requires-recent-login') {
      message = 'Please log in again before changing your password.';
    }
    
    return { success: false, message };
  }
};

/**
 * Generate a secure temporary password
 * @returns {string} - Secure temporary password
 */
const generateSecurePassword = () => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*';
  let password = '';
  
  // Ensure at least one of each required character type
  password += 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'[Math.floor(Math.random() * 26)]; // Uppercase
  password += 'abcdefghijklmnopqrstuvwxyz'[Math.floor(Math.random() * 26)]; // Lowercase
  password += '0123456789'[Math.floor(Math.random() * 10)]; // Number
  password += '!@#$%^&*'[Math.floor(Math.random() * 8)]; // Special char
  
  // Fill the rest randomly
  for (let i = 4; i < 12; i++) {
    password += chars[Math.floor(Math.random() * chars.length)];
  }
  
  // Shuffle the password
  return password.split('').sort(() => Math.random() - 0.5).join('');
};

/**
 * Validate password strength
 * @param {string} password - Password to validate
 * @returns {Object} - Validation result
 */
export const validatePassword = (password) => {
  const minLength = 8;
  const hasUpperCase = /[A-Z]/.test(password);
  const hasLowerCase = /[a-z]/.test(password);
  const hasNumbers = /\d/.test(password);
  const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);
  
  const isValid = password.length >= minLength && 
                  hasUpperCase && 
                  hasLowerCase && 
                  hasNumbers && 
                  hasSpecialChar;
  
  return {
    isValid,
    errors: [
      password.length < minLength && `At least ${minLength} characters`,
      !hasUpperCase && 'At least one uppercase letter',
      !hasLowerCase && 'At least one lowercase letter',
      !hasNumbers && 'At least one number',
      !hasSpecialChar && 'At least one special character'
    ].filter(Boolean)
  };
}; 